import React, { useEffect, useRef, useState } from 'react';
import type { PortfolioItem } from '../types/interfaces';
import { usePortfolioSnapshots } from '../hooks/usePortfolioSnapshots';
import { formatCurrency } from '../utils/currency';

interface PortfolioAnalyticsProps {
  portfolio: PortfolioItem[];
  currency?: string;
  isUnlocked: boolean;
  onUnlock: () => Promise<void>;
}

declare global {
  interface Window {
    Chart: any;
  }
}

export default function PortfolioAnalytics({ portfolio, currency = 'usd', isUnlocked, onUnlock }: PortfolioAnalyticsProps) {
  const { snapshots, currentSnapshot, getSnapshotsInRange, getBestWorstPerformers } = usePortfolioSnapshots(portfolio);
  const [selectedTimeframe, setSelectedTimeframe] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  
  const valueChartRef = useRef<HTMLCanvasElement>(null);
  const allocationChartRef = useRef<HTMLCanvasElement>(null);
  const performanceChartRef = useRef<HTMLCanvasElement>(null);
  
  const valueChartInstanceRef = useRef<any>(null);
  const allocationChartInstanceRef = useRef<any>(null);
  const performanceChartInstanceRef = useRef<any>(null);

  // Render portfolio value chart over time
  useEffect(() => {
    if (!isUnlocked || !currentSnapshot || !valueChartRef.current) return;

    const rangeSnapshots = selectedTimeframe === 'all' 
      ? snapshots 
      : getSnapshotsInRange(selectedTimeframe === '7d' ? 7 : selectedTimeframe === '30d' ? 30 : 90);

    if (rangeSnapshots.length === 0) return;

    if (valueChartInstanceRef.current) {
      valueChartInstanceRef.current.destroy();
    }

    const ctx = valueChartRef.current.getContext('2d');
    valueChartInstanceRef.current = new window.Chart(ctx, {
      type: 'line',
      data: {
        labels: rangeSnapshots.map(s => {
          const date = new Date(s.timestamp);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }),
        datasets: [
          {
            label: 'Portfolio Value',
            data: rangeSnapshots.map(s => s.totalValue),
            borderColor: 'rgb(99, 102, 241)',
            backgroundColor: 'rgba(99, 102, 241, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 5
          },
          {
            label: 'Total Cost',
            data: rangeSnapshots.map(s => s.totalCost),
            borderColor: 'rgb(139, 92, 246)',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 5
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              color: '#cbd5e1'
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#f1f5f9',
            borderColor: '#6366f1',
            borderWidth: 1,
            callbacks: {
              label: function(context: any) {
                return `${context.dataset.label}: ${formatCurrency(context.parsed.y, currency)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: '#cbd5e1' },
            grid: { color: 'rgba(203, 213, 225, 0.1)' }
          },
          y: {
            ticks: { 
              color: '#cbd5e1',
              callback: function(value: any) {
                return formatCurrency(value, currency, { compact: true });
              }
            },
            grid: { color: 'rgba(203, 213, 225, 0.1)' }
          }
        }
      }
    });

    return () => {
      if (valueChartInstanceRef.current) {
        valueChartInstanceRef.current.destroy();
      }
    };
  }, [isUnlocked, currentSnapshot, snapshots, selectedTimeframe, getSnapshotsInRange, currency]);

  // Render asset allocation pie chart
  useEffect(() => {
    if (!isUnlocked || !currentSnapshot || !allocationChartRef.current) return;

    if (allocationChartInstanceRef.current) {
      allocationChartInstanceRef.current.destroy();
    }

    const ctx = allocationChartRef.current.getContext('2d');
    
    const colors = [
      'rgb(99, 102, 241)',
      'rgb(139, 92, 246)',
      'rgb(236, 72, 153)',
      'rgb(251, 146, 60)',
      'rgb(34, 197, 94)',
      'rgb(59, 130, 246)',
      'rgb(168, 85, 247)',
      'rgb(239, 68, 68)'
    ];

    allocationChartInstanceRef.current = new window.Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: currentSnapshot.items.map(item => item.symbol.toUpperCase()),
        datasets: [{
          data: currentSnapshot.items.map(item => item.value),
          backgroundColor: currentSnapshot.items.map((_, i) => colors[i % colors.length]),
          borderColor: 'rgba(15, 23, 42, 0.8)',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              color: '#cbd5e1',
              padding: 15,
              font: { size: 12 }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#f1f5f9',
            borderColor: '#6366f1',
            borderWidth: 1,
            callbacks: {
              label: function(context: any) {
                const label = context.label || '';
                const value = context.parsed || 0;
                const percent = currentSnapshot.totalValue > 0 
                  ? ((value / currentSnapshot.totalValue) * 100).toFixed(1) 
                  : '0';
                return `${label}: ${formatCurrency(value, currency)} (${percent}%)`;
              }
            }
          }
        }
      }
    });

    return () => {
      if (allocationChartInstanceRef.current) {
        allocationChartInstanceRef.current.destroy();
      }
    };
  }, [isUnlocked, currentSnapshot, currency]);

  // Render performance comparison (best/worst)
  useEffect(() => {
    if (!isUnlocked || !currentSnapshot || !performanceChartRef.current) return;

    const { best, worst } = getBestWorstPerformers();
    if (best.length === 0 && worst.length === 0) return;

    if (performanceChartInstanceRef.current) {
      performanceChartInstanceRef.current.destroy();
    }

    const ctx = performanceChartRef.current.getContext('2d');
    
    const allPerformers = [...best, ...worst.slice().reverse()];
    const labels = allPerformers.map(p => p.symbol.toUpperCase());
    const roiData = allPerformers.map(p => {
      return p.cost > 0 ? (p.profit / p.cost) * 100 : 0;
    });

    performanceChartInstanceRef.current = new window.Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'ROI %',
          data: roiData,
          backgroundColor: roiData.map(roi => 
            roi >= 0 ? 'rgba(34, 197, 94, 0.7)' : 'rgba(239, 68, 68, 0.7)'
          ),
          borderColor: roiData.map(roi => 
            roi >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
          ),
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#f1f5f9',
            borderColor: '#6366f1',
            borderWidth: 1,
            callbacks: {
              label: function(context: any) {
                const roi = context.parsed.y;
                return `ROI: ${roi >= 0 ? '+' : ''}${roi.toFixed(2)}%`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: '#cbd5e1' },
            grid: { display: false }
          },
          y: {
            ticks: { 
              color: '#cbd5e1',
              callback: function(value: any) {
                return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
              }
            },
            grid: { color: 'rgba(203, 213, 225, 0.1)' }
          }
        }
      }
    });

    return () => {
      if (performanceChartInstanceRef.current) {
        performanceChartInstanceRef.current.destroy();
      }
    };
  }, [isUnlocked, currentSnapshot, getBestWorstPerformers]);

  if (!isUnlocked) {
    return (
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 border-2 border-primary/10 shadow-bubble-lg">
        <div className="text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            Portfolio Analytics
          </h3>
          <p className="text-text-secondary mb-6">
            Unlock advanced portfolio insights, charts, and analytics for 500 satoshis
          </p>
          <button
            onClick={onUnlock}
            className="px-8 py-4 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-base font-semibold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95"
          >
            🔓 Unlock Analytics (500 sats)
          </button>
        </div>
      </div>
    );
  }

  if (!currentSnapshot) {
    return (
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 border-2 border-primary/10 shadow-bubble-lg text-center">
        <p className="text-text-secondary">No portfolio data available yet. Add coins to see analytics.</p>
      </div>
    );
  }

  const { best, worst } = getBestWorstPerformers();
  const rangeSnapshots = selectedTimeframe === 'all' 
    ? snapshots 
    : getSnapshotsInRange(selectedTimeframe === '7d' ? 7 : selectedTimeframe === '30d' ? 30 : 90);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-bg to-bg-secondary p-6 rounded-2xl border-2 border-primary/10 shadow-bubble">
          <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Total Value</div>
          <div className="text-2xl font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">
            {formatCurrency(currentSnapshot.totalValue, currency)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-bg to-bg-secondary p-6 rounded-2xl border-2 border-primary/10 shadow-bubble">
          <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Total Profit</div>
          <div className={`text-2xl font-bold ${
            currentSnapshot.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {currentSnapshot.totalProfit >= 0 ? '+' : ''}
            {formatCurrency(currentSnapshot.totalProfit, currency)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-bg to-bg-secondary p-6 rounded-2xl border-2 border-primary/10 shadow-bubble">
          <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">ROI</div>
          <div className={`text-2xl font-bold ${
            currentSnapshot.totalProfitPercent >= 0 ? 'text-green-400' : 'text-red-400'
          }`}>
            {currentSnapshot.totalProfitPercent >= 0 ? '+' : ''}
            {currentSnapshot.totalProfitPercent.toFixed(2)}%
          </div>
        </div>
        <div className="bg-gradient-to-br from-bg to-bg-secondary p-6 rounded-2xl border-2 border-primary/10 shadow-bubble">
          <div className="text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Assets</div>
          <div className="text-2xl font-bold bg-gradient-to-r from-text to-primary bg-clip-text text-transparent">
            {portfolio.length}
          </div>
        </div>
      </div>

      {/* Portfolio Value Chart */}
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-text">Portfolio Value Over Time</h3>
          <div className="flex gap-2">
            {(['7d', '30d', '90d', 'all'] as const).map(timeframe => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeframe(timeframe)}
                className={`px-4 py-2 rounded-full border-2 transition-all duration-300 text-sm font-semibold ${
                  selectedTimeframe === timeframe
                    ? 'bg-gradient-to-r from-primary to-primary-light border-primary text-white'
                    : 'bg-transparent border-border text-text-secondary hover:border-primary'
                }`}
              >
                {timeframe === 'all' ? 'All' : timeframe}
              </button>
            ))}
          </div>
        </div>
        <div style={{ height: '300px', position: 'relative' }}>
          <canvas ref={valueChartRef}></canvas>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Asset Allocation */}
        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg">
          <h3 className="text-xl font-bold text-text mb-4">Asset Allocation</h3>
          <div style={{ height: '300px', position: 'relative' }}>
            <canvas ref={allocationChartRef}></canvas>
          </div>
        </div>

        {/* Performance Comparison */}
        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg">
          <h3 className="text-xl font-bold text-text mb-4">Top & Bottom Performers</h3>
          <div style={{ height: '300px', position: 'relative' }}>
            <canvas ref={performanceChartRef}></canvas>
          </div>
        </div>
      </div>

      {/* Best/Worst Performers List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg">
          <h3 className="text-xl font-bold text-text mb-4 flex items-center gap-2">
            <span className="text-green-400">🏆</span> Best Performers
          </h3>
          <div className="space-y-3">
            {best.map((item, index) => {
              const roi = item.cost > 0 ? (item.profit / item.cost) * 100 : 0;
              return (
                <div key={item.id} className="flex items-center justify-between p-4 bg-bg rounded-2xl border-2 border-border">
                  <div>
                    <div className="font-bold text-text">{item.symbol.toUpperCase()}</div>
                    <div className="text-sm text-text-secondary">
                      {formatCurrency(item.value, currency)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-400">
                      +{roi.toFixed(2)}%
                    </div>
                    <div className="text-xs text-text-secondary">
                      {formatCurrency(item.profit, currency)}
                    </div>
                  </div>
                </div>
              );
            })}
            {best.length === 0 && (
              <p className="text-text-secondary text-center py-4">No data available</p>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-6 border-2 border-primary/10 shadow-bubble-lg">
          <h3 className="text-xl font-bold text-text mb-4 flex items-center gap-2">
            <span className="text-red-400">📉</span> Worst Performers
          </h3>
          <div className="space-y-3">
            {worst.map((item, index) => {
              const roi = item.cost > 0 ? (item.profit / item.cost) * 100 : 0;
              return (
                <div key={item.id} className="flex items-center justify-between p-4 bg-bg rounded-2xl border-2 border-border">
                  <div>
                    <div className="font-bold text-text">{item.symbol.toUpperCase()}</div>
                    <div className="text-sm text-text-secondary">
                      {formatCurrency(item.value, currency)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-red-400">
                      {roi.toFixed(2)}%
                    </div>
                    <div className="text-xs text-text-secondary">
                      {formatCurrency(item.profit, currency)}
                    </div>
                  </div>
                </div>
              );
            })}
            {worst.length === 0 && (
              <p className="text-text-secondary text-center py-4">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

